import { Group, GroupOpportunity } from "@entities";
import { groupRepo } from "@repositories";
import { useQuery } from "@tanstack/react-query";
import { useOrganization } from "../contexts/OrganizationProvider";
import {
  ADMIN_LIST_PAGE_SIZE,
  ADMIN_LIST_QUERY_OPTIONS,
  findAllPages,
} from "../helpers/admin-list-query";

export const calendarGroupsQueryKey = (
  organizationId: string | undefined,
  startMs?: number,
  endMs?: number,
) => ["calendar-groups", organizationId, startMs, endMs] as const;

export function useCalendarGroups(range?: { startDate?: Date; endDate?: Date }) {
  const { currOrganization } = useOrganization();
  const startDate = range?.startDate;
  const endDate = range?.endDate;

  return useQuery({
    queryKey: calendarGroupsQueryKey(
      currOrganization?.id,
      startDate?.getTime(),
      endDate?.getTime(),
    ),
    queryFn: () =>
      findAllPages(
        (page) =>
          groupRepo.find({
            where: Group.hyperFilter({
              organization: { $id: currOrganization!.id },
              isDeleted: false,
              volunteerOrganizationId: null,
              $or: [
                {
                  preferredStartDate: { $gte: startDate, $lte: endDate },
                },
                {
                  $and: [
                    { preferredStartDate: { $lte: endDate } },
                    { preferredEndDate: { $gte: startDate } },
                  ],
                },
                {
                  opportunities: {
                    $some: {
                      opportunity: {
                        $is: {
                          startDate: { $lte: endDate },
                          endDate: { $gte: startDate },
                        },
                      },
                    },
                  },
                },
              ],
            }),
            include: {
              status: true,
              opportunities: {
                where: GroupOpportunity.hyperFilter({
                  $or: [
                    {
                      opportunity: {
                        $is: {
                          startDate: { $lte: endDate },
                          endDate: { $gte: startDate },
                        },
                      },
                    },
                    { opportunity: { $is: { hasTime: false } } },
                  ],
                }),
                include: { opportunity: true },
              },
            },
            limit: ADMIN_LIST_PAGE_SIZE,
            page,
          }),
        ADMIN_LIST_PAGE_SIZE,
      ),
    enabled: !!currOrganization?.id && !!startDate && !!endDate,
    ...ADMIN_LIST_QUERY_OPTIONS,
    staleTime: 1000 * 60 * 5,
  });
}
